import { usePublicLanguage } from '../../public-site/PublicLanguage';
import React from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Link as MuiLink,
  Grid,
  useTheme,
} from '@mui/material';
import { Facebook, Instagram } from '@mui/icons-material';
import revelationaryLogo from '../../assets/revelationary-logo-compact.webp';
import bpLogoDark from '../../assets/BP_mono-wht.png';
import bpLogoLight from '../../assets/BP_mono.png';

const footerNavLinkSx = {
  color: '#A1A1A1',
  fontWeight: 400,
  fontSize: '13px',
  whiteSpace: 'nowrap' as const,
  '&:hover': {
    textDecoration: 'underline',
    color: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'light' ? '#212121' : '#FFF',
  },
};

const Footer = () => {
  const { t, href } = usePublicLanguage();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: (theme) => (theme.palette.mode === 'light' ? '#FFF' : '#212121'),
        py: { xs: 3, sm: 4 },
        borderTop: '1px solid rgba(255, 255, 255, 0.12)',
        mt: '1px',
      }}
    >
      <Container maxWidth="xl">
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Grid item xs={12} md="auto">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <MuiLink
                href={href('/')}
                style={{
                  textDecoration: 'none',
                  color: isDarkMode ? '#FFF' : '#212121',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <img
                  src={revelationaryLogo}
                  alt="revelationary"
                  width="376"
                  height="82"
                  style={{
                    height: '32px',
                    width: 'auto',
                    marginRight: 10,
                    marginLeft: 10,
                    filter: isDarkMode ? 'invert(1)' : 'none',
                  }}
                />
              </MuiLink>
              <Typography
                variant="body2"
                sx={{
                  ml: 1.5,
                  color: 'grey.600',
                  fontSize: { xs: '12px', sm: '14px' },
                }}
              >
                info@revelationary.org
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md="auto">
            <Box
              component="nav"
              aria-label={t('Footer')}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-end' },
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2.5, md: 3 },
                rowGap: { xs: 1, sm: 1.5 },
                px: { xs: 1, sm: 0 },
              }}
            >
              <MuiLink href={href('/')} underline="none" sx={footerNavLinkSx}>
                {t('Home')}
              </MuiLink>
              <MuiLink href={href('/about/')} underline="none" sx={footerNavLinkSx}>
                {t('About')}
              </MuiLink>
              <MuiLink href={href('/resources/')} underline="none" sx={footerNavLinkSx}>
                {t('Resources')}
              </MuiLink>
              <MuiLink href={href('/faq/')} underline="none" sx={footerNavLinkSx}>
                {t('FAQ')}
              </MuiLink>
              <MuiLink href={href('/contact/')} underline="none" sx={footerNavLinkSx}>
                {t('Contact')}
              </MuiLink>
              <MuiLink href={href('/study/')} underline="none" sx={footerNavLinkSx}>
                {t('Bible study')}
              </MuiLink>
              <MuiLink href={href('/blog/')} underline="none" sx={footerNavLinkSx}>
                {t('Blog')}
              </MuiLink>
              <MuiLink href={href('/games/')} underline="none" sx={footerNavLinkSx}>
                {t('Games')}
              </MuiLink>
              <MuiLink href={href('/privacy-policy/')} underline="none" sx={footerNavLinkSx}>
                {t('Privacy')}
              </MuiLink>
              <MuiLink href={href('/terms-of-service/')} underline="none" sx={footerNavLinkSx}>
                {t('Terms')}
              </MuiLink>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.300', my: 3 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 2 },
            mb: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'grey.600',
              fontSize: { xs: '12px', sm: '14px' },
              mb: 0,
              flexShrink: 0,
            }}
          >
            {t('Friends with')}
          </Typography>
          <MuiLink
            href="https://bibleproject.com"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <img
              src={isDarkMode ? bpLogoDark : bpLogoLight}
              alt={t('Bible Project')}
              width="120"
              height="40"
              style={{ height: '40px', width: 'auto', maxWidth: '100%' }}
            />
          </MuiLink>
        </Box>

        <Grid container justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography
              component="div"
              variant="body2"
              sx={{ color: 'grey.600', fontSize: { xs: '12px', sm: '14px' } }}
            >
              © {new Date().getFullYear()}
              {t('Revelationary Ltd. All rights reserved.')}
              <br />
              {t('167-169 Great Portland Street 5th Floor London W1W 5PF')}
              <br />
              {t('Company Number 16288234')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '10px', sm: '11px' },
                mt: 2,
                color: 'grey.600',
              }}
            >
              {t(
                'The views, opinions, and media shared on this website are presented solely from a biblical perspective for educational and religious discussion purposes, and do not represent professional, political, or legal advice; the website and its operators assume no liability or responsibility for any interpretation, use, or consequences arising from the content.',
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} md="auto">
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                aria-label={t('Revelationary on Facebook')}
                href="https://www.facebook.com/revelationary.online/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#A1A1A1',
                  '&:hover': {
                    color: (theme) => (theme.palette.mode === 'light' ? '#212121' : '#FFF'),
                  },
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                aria-label={t('Revelationary on Instagram')}
                href="https://www.instagram.com/revelationary.online/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#A1A1A1',
                  '&:hover': {
                    color: (theme) => (theme.palette.mode === 'light' ? '#212121' : '#FFF'),
                  },
                }}
              >
                <Instagram />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Footer;
